import { BRAND_CONFIG } from '@@brand'
import { AddressItemType } from '@@types'
import { Map, Placemark, YMaps } from '@pbe/react-yandex-maps'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAddressStore, useFetchMyAddresses } from '@entities/Address'

import Button from '@shared/ui/button'

import styles from './DeliveryView.module.scss'



const DEFAULT_CENTER: [number, number] = [
  Number(import.meta.env.VITE_MAP_DEFAULT_LAT) || 50.300377,
  Number(import.meta.env.VITE_MAP_DEFAULT_LNG) || 57.154555,
]
const COORDINATE_THRESHOLD = 0.0001

interface MapDeliveryProps {
  newAddress: boolean;
  onChangeAddressMap: ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => void;
  geoPoint?: { latitude: number; longitude: number } | null;
}

export default function MapDelivery (props: MapDeliveryProps) {
  const { setSelectedAddress, setDeliveryType } = useAddressStore()
  const { data: addressData } = useFetchMyAddresses()
  const { t: tc } = useTranslation('common')
  const { newAddress, onChangeAddressMap, geoPoint } = props
  const mapRef = useRef<ymaps.Map | null>(null)
  const [ isMapReady, setMapReady ] = useState(false)
  // Инициализируем центр карты из geoPoint если есть
  const initialCenter: [number, number] = geoPoint 
    ? [ geoPoint.latitude, geoPoint.longitude ] 
    : DEFAULT_CENTER
  
  const [ mapCenter, _setMapCenter ] = useState<number[]>(initialCenter)
  const [ initialCoords, setInitialCoords ] = useState<number[] | null>(geoPoint ? initialCenter : null)
  const [ currentCoords, setCurrentCoords ] = useState<number[] | null>(geoPoint ? initialCenter : null)

  // Центрируем карту при изменении geoPoint
  useEffect(() => {
    if (geoPoint && isMapReady && mapRef.current) {
      const newCenter = [ geoPoint.latitude, geoPoint.longitude ]
      mapRef.current.setCenter(newCenter, 16, { duration: 300 })
      setCurrentCoords(newCenter)
      setInitialCoords(newCenter) // Синхронизируем чтобы кнопка "Выбрать адрес" не показывалась
    }
  }, [ geoPoint, isMapReady ])

  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       const { latitude, longitude } = position.coords;
  //       setMapCenter([latitude, longitude]);
  //     },
  //     () => {
  //       setMapCenter(DEFAULT_CENTER);
  //     },
  //   );
  // }, []);

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return

    const map = mapRef.current

    const handleMapMove = async () => {
      const center = map.getCenter()
      setCurrentCoords(center)
    }

    map.events.add('actionend', handleMapMove)

    return () => {
      map.events.remove('actionend', handleMapMove)
    }
  }, [ isMapReady ])

  const handleAddressSelect = () => {
    if (currentCoords) {
      onChangeAddressMap({
        latitude: currentCoords[0],
        longitude: currentCoords[1],
      })
      setInitialCoords(currentCoords)
    }
  }

  const hasCoordinatesChanged = () => {
    if (!initialCoords || !currentCoords) return false

    return (
      Math.abs(initialCoords[0] - currentCoords[0]) > COORDINATE_THRESHOLD ||
      Math.abs(initialCoords[1] - currentCoords[1]) > COORDINATE_THRESHOLD
    )
  }

  return (
    <div className={styles.map}>
      <YMaps
        query={{
          apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY,
          load: 'package.full',
        }}
      >
        <Map
          defaultState={{
            center: mapCenter,
            zoom: 14,
            controls: [ 'zoomControl' ],
          }}
          width="100%"
          height="100%"
          instanceRef={(ref) => {
            if (ref) {
              mapRef.current = ref
              setMapReady(true)

              if (!initialCoords) {
                setInitialCoords(mapCenter)
                setCurrentCoords(mapCenter)
              }
            }
          }}
          options={{
            suppressMapOpenBlock: true,
            copyrightLogoVisible: false,
            copyrightUaVisible: false,
          }}
          
        >
          {!newAddress &&
            addressData?.myAddresses?.map((item) => {
              return (
                <Placemark
                  key={item?.id}
                  geometry={[
                    item?.customerGeoPoint?.latitude,
                    item?.customerGeoPoint?.longitude,
                  ]}
                  options={{
                    iconLayout: 'default#image',
                    iconImageHref: BRAND_CONFIG.images.mapPinActive,
                    iconImageSize: [ 36, 55 ],
                    iconImageOffset: [ -16, -32 ],
                  }}
                  onClick={() => {
                    setSelectedAddress(item as AddressItemType)
                    setDeliveryType('delivery')
                  }}
                />
              )
            })}
        </Map>
      </YMaps>

      {newAddress && (
        <div className={styles.map__center}>
          <div className={styles['map__center-marker']}></div>
          {hasCoordinatesChanged() && (
            <Button
              type="button"
              className={styles['map__center-select']}
              onClick={handleAddressSelect}
            >
              {tc('address.choose')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
